import { Fragment, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IonButton, IonIcon, IonItem, IonCheckbox, IonSearchbar, IonSpinner } from "@ionic/react";
import { searchOutline, arrowForwardOutline } from "ionicons/icons";
import { uiAvatar } from "./SidebarProfileCard";
import { NoResults } from "./NotificationsDrawer";

// ── Topbar global search (whodat admin port), shared by the admin dashboard
// and /app ─────────────────────────────────────────────────────────────────
// Icon-only until clicked, then the input scales in from the right over the
// toolbar's middle (the title/tabs fade out) and grouped results drop beneath
// it. While the box is empty it lists recently viewed results with a
// select-all sub-bar and Delete All / Delete Selected. Arrow keys move the
// highlight, Enter opens, Escape closes, Cmd/Ctrl+K opens from anywhere.
//
// useTopbarSearch({ groups, search, onOpen, recentsUid, recentsPrefix, placeholder, dark })
//   groups:  { type: { label } } in display order
//   search:  async (query) => [{ type, title, subtitle, route }]
//   onOpen:  (result) => void
// returns { open, setOpen, close, midRef, overlay, button, panel } — put
// `midRef` on the toolbar's middle wrapper, render `overlay` inside it,
// `button` in the end slot and `panel` anywhere after the header.

// Recently-viewed results, kept per person (keyed by their id) so one
// account's history never shows for another on a shared machine.
const recentsKey = (prefix, uid) => `${prefix}:${uid || "anon"}`;
export const loadRecents = (prefix, uid) => {
  try { return JSON.parse(localStorage.getItem(recentsKey(prefix, uid)) || "[]"); } catch { return []; }
};
export const saveRecent = (prefix, uid, r) => {
  try {
    const list = [{ type: r.type, title: r.title, subtitle: r.subtitle, route: r.route },
      ...loadRecents(prefix, uid).filter((x) => x.route !== r.route)].slice(0, 8);
    localStorage.setItem(recentsKey(prefix, uid), JSON.stringify(list));
    return list;
  } catch { return []; }
};
export const saveRecentsList = (prefix, uid, list) => {
  try { localStorage.setItem(recentsKey(prefix, uid), JSON.stringify(list)); } catch { /* quota */ }
  return list;
};

// The expanding search box: scales in over a toolbar's middle (put it inside
// an `.admin-topbar-mid` wrapper next to the `.admin-topbar-mid-content`
// that fades out) with an always-visible cancel button. Shared by the
// topbar search and the Tutorials modal.
export function SearchOverlay({ open, inputRef, value, onInput, onKeyDown, onCancel, placeholder, dark }) {
  return (
    <div className={`admin-topbar-search${open ? " is-open" : ""}`}>
      <IonSearchbar
        ref={inputRef}
        showClearButton="never"
        value={value}
        onIonInput={(e) => onInput(e.detail.value ?? "")}
        onKeyDown={onKeyDown}
        onIonCancel={onCancel}
        showCancelButton="always"
        placeholder={placeholder}
        style={{
          "--background": dark ? "var(--ion-background-color)" : "#ffffff",
          "--color": dark ? "#ffffff" : "#000000",
          "--placeholder-color": dark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.4)",
          "--icon-color": dark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)",
          "--cancel-button-color": dark ? "#ffffff" : "var(--ion-color-primary)",
          "--border-radius": "0",
          "--box-shadow": "0 4px 24px rgba(0,0,0,0.18)",
          padding: "0 4px",
        }}
      />
    </div>
  );
}

// The search icon that opens the box; hidden while it is open. On the green
// topbar it is white; pass `color` (e.g. "dark") for an ordinary toolbar.
export function SearchButton({ open, onClick, color }) {
  return (
    <IonButton
      fill="clear"
      aria-label="Search"
      color={color}
      shape={color ? "round" : undefined}
      onClick={onClick}
      style={{ ...(color ? {} : { "--color": "rgba(255,255,255,0.8)" }), "--border-radius": "50%", display: open ? "none" : undefined }}
    >
      <span slot="icon-only" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: "24px" }}>
        <IonIcon icon={searchOutline} style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />
      </span>
    </IonButton>
  );
}

export function useTopbarSearch({ groups, search, onOpen, recentsUid, recentsPrefix = "mintslip:adminRecent", placeholder = "Search…", dark = false, minChars = 2 }) {
  const order = Object.keys(groups);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [recents, setRecents] = useState([]);
  const [recentSel, setRecentSel] = useState(() => new Set()); // ticked recently-viewed rows (by route)
  const [activeIdx, setActiveIdx] = useState(0);
  const [panelRect, setPanelRect] = useState(null);
  const inputRef = useRef(null);
  const midRef = useRef(null);

  // The results panel is portaled to <body> (ion-toolbar clips overflow), so
  // it tracks the search box's on-screen rect instead of using absolute CSS.
  const measure = () => {
    const r = midRef.current?.getBoundingClientRect();
    if (r) setPanelRect({ top: r.bottom + 10, left: r.left, width: r.width });
  };

  useEffect(() => {
    if (!open) return undefined;
    setRecents(loadRecents(recentsPrefix, recentsUid));
    measure();
    const t = setTimeout(() => inputRef.current?.setFocus?.(), 80);
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    return () => { clearTimeout(t); window.removeEventListener("resize", onResize); };
  }, [open]); // eslint-disable-line

  // Cmd/Ctrl+K opens search from anywhere
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Live search, debounced
  useEffect(() => {
    const q = query.trim();
    setActiveIdx(0);
    if (q.length < minChars) { setResults([]); setSearching(false); return undefined; }
    setSearching(true);
    let alive = true;
    const t = setTimeout(() => {
      Promise.resolve()
        .then(() => search(q))
        .then((rows) => { if (alive) setResults(Array.isArray(rows) ? rows : []); })
        .catch(() => { if (alive) setResults([]); })
        .finally(() => { if (alive) setSearching(false); });
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [query]); // eslint-disable-line

  // What the panel is currently listing: live results while typing, the
  // recently-viewed list while the box is focused but empty. Flattened in
  // render order so arrow keys/Enter address the same rows the user sees.
  const showingRecents = query.trim().length < minChars;
  const flatRows = showingRecents ? recents : order.flatMap((type) => results.filter((r) => r.type === type));

  const close = () => { setOpen(false); setQuery(""); setRecentSel(new Set()); };
  const openResult = (r) => {
    setRecents(saveRecent(recentsPrefix, recentsUid, r));
    setOpen(false); setQuery(""); setResults([]); setRecentSel(new Set());
    onOpen(r);
  };

  // Recently viewed: tick rows (or all of them from the sub-bar) and delete
  // the ticked ones, or everything when nothing is ticked.
  const recentRoutes = recents.map((r) => r.route);
  const selectedRecents = recentRoutes.filter((rt) => recentSel.has(rt));
  const allRecentsSelected = recentRoutes.length > 0 && selectedRecents.length === recentRoutes.length;
  const toggleRecent = (route) => setRecentSel((prev) => {
    const next = new Set(prev);
    if (next.has(route)) next.delete(route); else next.add(route);
    return next;
  });
  const toggleAllRecents = () => setRecentSel(allRecentsSelected ? new Set() : new Set(recentRoutes));
  const deleteRecents = () => {
    const keep = selectedRecents.length ? recents.filter((r) => !recentSel.has(r.route)) : [];
    setRecents(saveRecentsList(recentsPrefix, recentsUid, keep));
    setRecentSel(new Set());
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") { close(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, flatRows.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    if (e.key === "Enter" && flatRows.length) openResult(flatRows[Math.min(activeIdx, flatRows.length - 1)]);
  };

  // One search-result row: an IonItem (native Ionic ripple) with the avatar;
  // `is-active` tracks the arrow-key/hover selection, Enter opens it. Recents
  // rows (`selectable`) carry an Ionic checkbox on the left that ticks the row
  // for the sub-bar's Delete Selected without opening it.
  const renderRow = (r, idx, selectable = false) => (
    <IonItem
      key={`${r.type}-${idx}`}
      button
      detail={false}
      lines="none"
      className={`admin-search-row${idx === activeIdx ? " is-active" : ""}${selectable && recentSel.has(r.route) ? " is-selected" : ""}`}
      onMouseEnter={() => setActiveIdx(idx)}
      onClick={() => openResult(r)}
    >
      <div className="admin-search-row-inner">
        {selectable && (
          <IonCheckbox
            className="admin-search-row-check"
            aria-label={`Select ${r.title}`}
            checked={recentSel.has(r.route)}
            onClick={(e) => { e.stopPropagation(); toggleRecent(r.route); }}
          />
        )}
        <img className="admin-search-row-avatar" src={uiAvatar(r.title)} alt="" />
        <span className="admin-search-row-main">
          <span className="admin-search-row-title">{r.title}</span>
          {r.subtitle && <span className="admin-search-row-sub">{r.subtitle}</span>}
        </span>
        <IonIcon icon={arrowForwardOutline} className="admin-search-row-go" />
      </div>
    </IonItem>
  );

  const overlay = (
    <SearchOverlay
      open={open}
      inputRef={inputRef}
      value={query}
      onInput={setQuery}
      onKeyDown={onKeyDown}
      onCancel={close}
      placeholder={placeholder}
      dark={dark}
    />
  );

  const button = <SearchButton open={open} onClick={() => setOpen(true)} />;

  // Live results (or recently viewed, while empty) below the search box —
  // portaled out because ion-toolbar clips overflow. Into <ion-app>, where
  // the notifications drawer and the modals render, so their z-indexes are
  // compared with this one (a <body>-level panel painted over the drawer).
  const panel = open && panelRect && (query.trim().length >= minChars || recents.length > 0) ? createPortal(
    <div className="admin-search-panel" style={{ top: panelRect.top, left: panelRect.left, width: panelRect.width }}>
      {showingRecents ? (
        <>
          {/* Sub-bar (notifications-drawer style): select-all on the left, a
              danger Delete All / Delete Selected on the right */}
          <div className="admin-search-subbar">
            <IonCheckbox
              className="admin-search-row-check admin-search-select-all"
              aria-label="Select all recently viewed"
              checked={allRecentsSelected}
              indeterminate={selectedRecents.length > 0 && !allRecentsSelected}
              onIonChange={toggleAllRecents}
            />
            <span className="admin-search-subbar-label">Recently viewed</span>
            <IonButton className="admin-search-subbar-btn" size="small" fill="clear" color="danger" onClick={deleteRecents}>
              {selectedRecents.length ? "Delete Selected" : "Delete All"}
            </IonButton>
          </div>
          {recents.map((r, i) => renderRow(r, i, true))}
        </>
      ) : searching && results.length === 0 ? (
        <div className="admin-search-empty"><IonSpinner name="crescent" style={{ width: 16, height: 16 }} /> Searching…</div>
      ) : results.length === 0 ? (
        <NoResults size="small" text={`No results found for "${query.trim()}"`} />
      ) : (
        order.map((type) => {
          const group = results.filter((r) => r.type === type);
          if (!group.length) return null;
          return (
            <Fragment key={type}>
              <div className="admin-search-subbar">
                <span className="admin-search-subbar-label">{groups[type].label}</span>
              </div>
              {group.map((r) => renderRow(r, flatRows.indexOf(r)))}
            </Fragment>
          );
        })
      )}
    </div>,
    document.querySelector("ion-app") || document.body
  ) : null;

  return { open, setOpen, close, midRef, overlay, button, panel };
}
